import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Custom hook to handle iOS Safari PWA scroll position issues
 * Specifically addresses the problem where content appears flush against
 * the header/banner after login navigation
 */
export const useIOSScrollFix = () => {
  const location = useLocation();
  const timeoutRefs = useRef([]);
  const isIOSRef = useRef(null);

  // Detect iOS Safari PWA
  const isIOSSafariPWA = () => {
    if (isIOSRef.current !== null) return isIOSRef.current;
    
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isStandalone = window.navigator.standalone === true;
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    
    isIOSRef.current = isIOS && (isStandalone || isSafari);
    return isIOSRef.current;
  };

  // Set the correct scroll position that matches the "after reload" behavior
  const forceIOSScrollReset = () => {
    if (!isIOSSafariPWA()) return;

    // Clear any existing timeouts
    timeoutRefs.current.forEach(clearTimeout);
    timeoutRefs.current = [];

    const setCorrectScrollPosition = () => {
      // The issue is that iOS Safari PWA preserves scroll position from login page
      // We need to scroll to the correct position that shows proper spacing
      // Based on the layout, the correct position should show the week title with proper spacing
      
      // Reset to top first, then let the layout settle
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      // Reset content wrapper to ensure it starts at the right position
      const contentWrapper = document.querySelector('[data-testid="content-wrapper"]');
      if (contentWrapper) {
        contentWrapper.scrollTop = 0;
        contentWrapper.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      }
    };

    // Immediate reset
    setCorrectScrollPosition();

    // Multiple attempts to ensure it sticks
    const delays = [16, 50, 100, 200, 400];
    delays.forEach(delay => {
      const timeoutId = setTimeout(setCorrectScrollPosition, delay);
      timeoutRefs.current.push(timeoutId);
    });
  };

  // Effect for route changes
  useEffect(() => {
    forceIOSScrollReset();
    
    return () => {
      // Cleanup timeouts on unmount or route change
      timeoutRefs.current.forEach(clearTimeout);
      timeoutRefs.current = [];
    };
  }, [location.pathname]);

  // Effect for initial mount
  useEffect(() => {
    if (isIOSSafariPWA()) {
      // Additional setup for iOS PWA
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          // App became visible, reset scroll position
          setTimeout(forceIOSScrollReset, 50);
        }
      };

      const handlePageShow = () => {
        // Page shown from cache, reset scroll position
        setTimeout(forceIOSScrollReset, 50);
      };

      const handleFocus = () => {
        // Window focused, reset scroll position
        setTimeout(forceIOSScrollReset, 50);
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('pageshow', handlePageShow);
      window.addEventListener('focus', handleFocus);

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('pageshow', handlePageShow);
        window.removeEventListener('focus', handleFocus);
        timeoutRefs.current.forEach(clearTimeout);
      };
    }
  }, []);

  return { forceIOSScrollReset };
};
